import React, { useEffect, useRef } from 'react';
import cn from '@/utils/cn';

// AIDEV-NOTE: Shared SPA checkbox. A real <input type="checkbox"> is kept in the DOM (visually
// hidden) rather than reimplemented with divs, so native form semantics, keyboard behaviour,
// the label association and `indeterminate` all still work. The visible box is its sibling, which
// is what lets the peer-* variants below drive it.
const boxClass = [
  'tw-relative tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-box-border tw-rounded',
  'tw-border tw-border-solid tw-border-spa-border-input tw-bg-spa-bg',
  'tw-flex tw-items-center tw-justify-center tw-transition-colors',
  'peer-hover:tw-border-spa-accent',
  'peer-checked:tw-bg-spa-accent-solid peer-checked:tw-border-spa-accent-solid',
  'peer-focus-visible:tw-outline peer-focus-visible:tw-outline-2',
  'peer-focus-visible:tw-outline-spa-accent peer-focus-visible:tw-outline-offset-2',
  'peer-disabled:tw-bg-spa-bg-disabled peer-disabled:tw-border-spa-bg-disabled',
  // Glyphs are stacked and revealed by state; indeterminate rules come last so they win when both.
  '[&>svg]:tw-opacity-0',
  'peer-checked:[&>.spa-checkbox-check]:tw-opacity-100',
  'peer-[:indeterminate]:tw-bg-spa-accent-solid peer-[:indeterminate]:tw-border-spa-accent-solid',
  'peer-[:indeterminate]:[&>.spa-checkbox-check]:tw-opacity-0',
  'peer-[:indeterminate]:[&>.spa-checkbox-dash]:tw-opacity-100',
].join(' ');

const glyphClass = 'tw-absolute tw-h-3 tw-w-3 tw-text-white';

interface SPACheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean;
  labelClassName?: string;
}

const SPACheckbox = React.forwardRef<HTMLInputElement, SPACheckboxProps>(
  ({ children, className, indeterminate = false, labelClassName, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    // `indeterminate` is a DOM property, not an attribute — React cannot set it declaratively.
    useEffect(() => {
      if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
      <label
        className={cn(
          // tw-relative anchors the sr-only input to this row. Without it the input's containing
          // block is the nearest positioned ancestor (in a dialog, the overflow-hidden panel), so
          // inputs deep in a scrolled list inflate that panel's scrollHeight and clicking one
          // focus-scrolls the hidden container, shoving the dialog header out of view.
          'tw-relative tw-inline-flex tw-items-start tw-gap-2 tw-m-0 tw-cursor-pointer',
          props.disabled && 'tw-cursor-not-allowed',
          className,
        )}
      >
        <input
          ref={(node) => {
            inputRef.current = node;
            if (typeof ref === 'function') ref(node);
            // eslint-disable-next-line no-param-reassign -- assigning a forwarded ref object is the documented pattern
            else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
          }}
          className="tw-peer tw-sr-only"
          type="checkbox"
          {...props}
        />
        <span className={boxClass}>
          <svg className={cn(glyphClass, 'spa-checkbox-check')} fill="none" viewBox="0 0 12 12">
            <path
              d="M2.5 6.4 4.8 8.7 9.5 3.6"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            />
          </svg>
          <svg className={cn(glyphClass, 'spa-checkbox-dash')} fill="none" viewBox="0 0 12 12">
            <path d="M3 6h6" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
          </svg>
        </span>
        {children != null && (
          <span
            className={cn(
              'tw-text-sm tw-leading-5 tw-text-spa-primary peer-disabled:tw-text-spa-disabled',
              labelClassName,
            )}
          >
            {children}
          </span>
        )}
      </label>
    );
  },
);

SPACheckbox.displayName = 'SPACheckbox';

export default SPACheckbox;
